"use client";
/* eslint-disable @next/next/no-location-assign-relative-destination -- a full navigation after logout clears all client-side private state */
import Link from "next/link";
import { Management, CourtDiary } from "./management";
import { useEffect, useState, useCallback } from "react";
import {
  ArrowUpRight,
  ArrowRight,
  LayoutDashboard,
  BriefcaseBusiness,
  FolderOpen,
  BookOpen,
  CheckSquare,
  Users,
  UserRound,
  Settings,
  Search,
  Plus,
  X,
  Menu,
  LogOut,
  Upload,
  FileText,
  Download,
  Trash2,
  RotateCcw,
  ChevronRight,
  Check,
  ShieldCheck,
  Activity,
  Cloud,
  LoaderCircle,
  PenLine,
  Scale,
} from "lucide-react";
import { Mark } from "./public-site";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
type Data = {
  assignee?: string;
  stage?: string;
  client?: string;
  court?: string;
  number?: string;
  status?: string;
  date?: string;
  priority?: string;
  body?: string;
  practice?: string;
  jurisdiction?: string;
  enrolment?: string;
  published?: boolean;
  parent?: string | null;
  folder?: string | null;
  size?: number;
  mime?: string;
  text?: string;
  version?: number;
  modified?: string;
  source_path?: string;
  document_id?: string;
  drive_file_id?: string;
  drive_path?: string;
  sha256?: string;
  review_status?: string;
  duplicate_group?: string;
  case_count?: number;
  case_numbers?: string;
  payment_advance?: string;
  payment_credit?: string;
  payment_amount?: string;
  recorded_dates?: string;
  payment_status?: string;
  location?: string;
};
export type Item = {
  id: string;
  kind: string;
  title: string;
  matter_id: string | null;
  data: Data;
  deleted: number;
  updated: string;
};
type Chamber = {
  id: string;
  name: string;
  plan: string;
  quota: number;
  used: number;
  members?: number;
};
type Me = {
  user: { name: string; role: string; id: string; chamber_id: string | null; access: string };
  chamber: Chamber | null;
};
type Audit = {
  action: string;
  created: string;
  target?: string;
  actor?: string;
};
type Office = {
  chambers: Chamber[];
  audit: Audit[];
  services: { name: string; state: string }[];
};
class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}
const nav = [
  ["briefing", "Daily briefing", LayoutDashboard],
  ["matter", "Matters", BriefcaseBusiness],
  ["document", "Documents", FolderOpen],
  ["task", "Tasks & dates", CheckSquare],
  ["client", "Clients", Users],
  ["diary", "Court diary", CheckSquare],
  ["team", "Chamber team", Users],
  ["support", "Service desk", Activity],
  ["research", "Research desk", BookOpen],
  ["profile", "Professional profile", UserRound],
  ["settings", "Chamber settings", Settings],
] as const;
async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch("/api/" + path, init);
  const d = (await r.json()) as T & { error?: string };
  if (!r.ok) throw new ApiError(r.status, d.error || "The request failed.");
  return d;
}
const send = (data: unknown, method = "POST"): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(data),
});
const bytes = (n: number) =>
  n >= 1e9
    ? (n / 1e9).toFixed(2) + " GB"
    : n >= 1e6
      ? (n / 1e6).toFixed(1) + " MB"
      : (n / 1000).toFixed(1) + " KB";
const date = (s?: string) =>
  s
    ? new Date(s + "T12:00:00").toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
      })
    : "No date";
function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      className="ws-icon-button"
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
export function Workspace({ office = false }: { office?: boolean }) {
  const [me, setMe] = useState<Me | null>(null),
    [items, setItems] = useState<Item[]>([]),
    [view, setView] = useState("briefing"),
    [officeData, setOffice] = useState<Office | null>(null),
    [events, setEvents] = useState<Audit[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [menu, setMenu] = useState(false),
    [query, setQuery] = useState(""),
    [command, setCommand] = useState(false),
    [selected, setSelected] = useState<Item | null>(null),
    [tab, setTab] = useState("overview"),
    [editing, setEditing] = useState<Partial<Item> | null>(null),
    [upload, setUpload] = useState(false),
    [busy, setBusy] = useState(false),
    [trash, setTrash] = useState(false),
    [folder, setFolder] = useState<string | null>(null),
    [filter, setFilter] = useState(""),
    [previewing, setPreviewing] = useState<Item | null>(null),
    [claimEmail, setClaimEmail] = useState(""),
    [claimResult, setClaimResult] = useState<{ email: string; message?: string } | null>(null),
    [research, setResearch] = useState<{
      message: string;
      sources: { id: string; title: string; excerpt: string }[];
    } | null>(null);
  const refresh = useCallback(async () => {
    const identity = await api<Me>("me");
    setMe(identity);
    if (office) {
      if (identity.user.role !== "admin")
        throw Error(
          "Platform administrator access required. Your advocate account cannot enter this office.",
        );
      setOffice(await api<Office>("office"));
    } else {
      if (identity.user.role === "admin")
        throw Error(
          "This is the advocate chamber. Use a separate advocate session to access matter records.",
        );
      let all: Item[] = [], cursor: string | null = null;
      for (let page = 0; page < 20; page++) {
        const d: { items: Item[]; hasMore: boolean; cursor?: string | null } = await api(
          "records" + (cursor ? "?cursor=" + encodeURIComponent(cursor) : ""),
        );
        all = all.concat(d.items);
        cursor = d.cursor ?? null;
        if (!d.hasMore || !cursor) break;
      }
      setItems(all);
      setSelected((current) => current ? all.find((item) => item.id === current.id && !item.deleted) ?? null : null);
      setEvents(await api<Audit[]>("audit"));
    }
  }, [office]);
  useEffect(() => {
    void Promise.resolve()
      .then(refresh)
      .then(() => {
        const hash = window.location.hash.slice(1);
        if (
          nav.some(([id]) => id === hash) ||
          (office && ["onboarding", "staff", "work", "storage"].includes(hash))
        )
          setView(hash);
      })
      .catch((e) => {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          window.location.replace("/sign-in" + (office ? "?office=1" : ""));
          return;
        }
        setError(e instanceof Error ? e.message : "Unable to open the workspace.");
      })
      .finally(() => setLoading(false));
  }, [refresh, office]);
  useEffect(() => {
    const onkey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommand((v) => !v);
      }
    };
    window.addEventListener("keydown", onkey);
    return () => window.removeEventListener("keydown", onkey);
  }, []);
  function go(v: string) {
    setView(v);
    setSelected(null);
    setQuery("");
    setMenu(false);
    setCommand(false);
    setFolder(null);
    setFilter("");
    setTrash(false);
    window.history.replaceState(null, "", "#" + v);
  }
  async function mutate(fn: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError("");
    try {
      await fn();
      await refresh();
      setNotice(message);
      setTimeout(() => setNotice(""), 4500);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save.");
    } finally {
      setBusy(false);
    }
  }
  const active = items.filter((i) => !i.deleted),
    matters = active.filter((i) => i.kind === "matter"),
    tasks = active.filter((i) => i.kind === "task"),
    documents = active.filter((i) => i.kind === "document");
  const list = items.filter(
    (i) =>
      i.kind === view &&
      Boolean(i.deleted) === trash &&
      (
        i.title +
        " " +
        JSON.stringify(i.data) +
        " " +
        (matters.find((m) => m.id === i.matter_id)?.title ?? "")
      )
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (!filter || i.matter_id === filter) &&
      (view !== "document" ||
        trash ||
        query.length > 0 ||
        folder === null ||
        (i.data.folder ?? null) === folder),
  );
  function edit(kind: string, matter_id: string | null = null) {
    setEditing({
      kind,
      title: "",
      matter_id,
      data:
        kind === "task"
          ? { status: "open", priority: "normal" }
          : kind === "event"
            ? { status: "open", date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10) }
          : kind === "matter"
            ? { status: "active" }
            : {},
    });
  }
  function openMatter(i: Item) {
    setSelected(i);
    setTab("overview");
    setView("matter");
    setCommand(false);
  }
  async function logout() {
    try {
      await api("session", { method: "DELETE" });
      window.location.href = "/sign-in" + (office ? "?office=1" : "");
    } catch (e) {
      setError((e as Error).message);
    }
  }
  if (loading)
    return (
      <div className="ws-loading">
        <Scale />
        <h2>Opening your workspace.</h2>
        <LoaderCircle className="ws-spin" />
      </div>
    );
  if (
    !me ||
    (office && me.user.role !== "admin") ||
    (!office && me.user.role === "admin")
  )
    return (
      <div className="ws-locked">
        <Mark />
        <LockCard office={office} message={error} />
      </div>
    );
  const title = office
    ? ({
        briefing: "Platform overview",
        onboarding: "Chamber onboarding",
        staff: "Our people",
        access: "Access & roles",
        work: "Office work",
        support: "Service desk",
        storage: "Storage allocations",
      }[view] ?? "Platform overview")
    : (nav.find((n) => n[0] === view)?.[1] ?? "Your workspace");
  return (
    <div className="ws-shell">
      <aside className={`ws-sidebar ${menu ? "open" : ""}`}>
        <Mark />
        <button
          className="ws-sidebar-close"
          onClick={() => setMenu(false)}
          aria-label="Close navigation"
        >
          <X />
        </button>
        <div className="ws-chamber-name">
          <small>{office ? "CHATAKE INNOWORKS" : "YOUR CHAMBER"}</small>
          <span>{office ? "Administration office" : me.chamber?.name}</span>
        </div>
        <nav aria-label="Workspace navigation">
          {office ? (
            <>
              <button
                className={view === "briefing" ? "active" : ""}
                onClick={() => go("briefing")}
              >
                <LayoutDashboard size={17} />
                Platform overview
              </button>
              {[
                ["onboarding", "Chamber onboarding"],
                ["staff", "Our people"],
                ["access", "Access & roles"],
                ["work", "Office work"],
                ["support", "Service desk"],
                ["storage", "Storage allocations"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  className={view === id ? "active" : ""}
                  onClick={() => go(id)}
                >
                  <BriefcaseBusiness size={17} />
                  {label}
                </button>
              ))}
              <Link href="/security">
                <ShieldCheck size={17} />
                Trust & privacy
                <ArrowUpRight size={12} />
              </Link>
            </>
          ) : (
            nav.map(([id, label, I]) => (
              <button
                className={view === id ? "active" : ""}
                key={id}
                aria-label={label}
                onClick={() => go(id)}
              >
                <I size={17} strokeWidth={1.5} />
                {label}
                {id === "task" && (
                  <span className="ws-nav-count">
                    {tasks.filter((t) => t.data.status !== "done").length}
                  </span>
                )}
              </button>
            ))
          )}
        </nav>
        <div className="ws-sidebar-bottom">
          {me.chamber && (
            <>
              <div className="ws-storage-label">
                <span>Chamber storage</span>
                <span>
                  {bytes(me.chamber.used)} / {bytes(me.chamber.quota)}
                </span>
              </div>
              <div className="ws-storage-bar">
                <span
                  style={{
                    width:
                      Math.min(
                        100,
                        (me.chamber.used / me.chamber.quota) * 100,
                      ) + "%",
                  }}
                />
              </div>
              <p>Verified Drive archive · chamber index on AWS</p>
            </>
          )}
          <div className="ws-user">
            <span className="dc-avatar">{office ? "CI" : "MJ"}</span>
            <span>
              {me.user.name}
              <small>
                {office ? "Platform administrator" : `${me.user.role.replaceAll("_", " ")} · ${me.user.access === "temporary" ? "pilot access" : "secure access"}`}
              </small>
            </span>
            <IconButton label="Sign out" onClick={logout}>
              <LogOut size={16} />
            </IconButton>
          </div>
          <Link className="ws-public-return" href="/">
            Back to the public website <ArrowUpRight size={13} />
          </Link>
        </div>
      </aside>
      {menu && (
        <button
          className="ws-scrim"
          aria-label="Close navigation"
          onClick={() => setMenu(false)}
        />
      )}
      <div className="ws-content">
        <header className="ws-topbar">
          <button
            className="ws-mobile-toggle"
            aria-label="Open workspace navigation"
            onClick={() => setMenu(true)}
          >
            <Menu />
          </button>
          <span>
            {office ? "OFFICE" : "CHAMBER"}
            <ChevronRight size={12} />
            <b>{title}</b>
          </span>
          <button
            className="ws-command-trigger"
            onClick={() => setCommand(true)}
          >
            <Search size={15} />
            <span>Find anything</span>
            <kbd>⌘ K</kbd>
          </button>
          <span className="ws-local-pill">{office ? "SECURE OFFICE" : "PRIVATE CHAMBER"}</span>
        </header>
        <main className="ws-main">
          {error && (
            <div className="ws-error" role="alert">
              {error}
              <button aria-label="Dismiss error" onClick={() => setError("")}>
                <X size={15} />
              </button>
            </div>
          )}
          {notice && (
            <div className="ws-notice" role="status">
              <Check size={15} />
              {notice}
            </div>
          )}
          {["team", "support"].includes(view) ||
          (office &&
            ["onboarding", "staff", "access", "work", "storage"].includes(view)) ? (
            <Management
              key={view}
              view={view}
              office={office}
              chamberId={me.chamber?.id ?? ""}
              chambers={officeData?.chambers}
              onRefresh={refresh}
            />
          ) : view === "diary" ? (
            <CourtDiary
              items={items}
              onAdd={() => edit("event")}
              onEdit={setEditing}
              onOpen={(i) => {
                if (i.kind === "matter") {
                  go("matter");
                  setSelected(i);
                } else setEditing(i);
              }}
            />
          ) : office && officeData ? (
            <>
              <Heading
                eyebrow="PLATFORM ADMINISTRATION"
                title="A clear view of the platform."
                detail="Chambers and operational status, with customer content kept separate."
                action={
                  <button
                    className="ws-button"
                    onClick={() =>
                      setEditing({ kind: "provision", title: "", data: {} })
                    }
                  >
                    <Plus size={16} />
                    Provision chamber
                  </button>
                }
              />
              <div className="ws-metrics">
                <Metric
                  label="Chambers"
                  value={String(officeData.chambers.length).padStart(2, "0")}
                  sub="Locally provisioned"
                />
                <Metric
                  label="Members"
                  value={String(
                    officeData.chambers.reduce(
                      (n, c) => n + (c.members ?? 0),
                      0,
                    ),
                  ).padStart(2, "0")}
                  sub="Chamber memberships"
                />
                <Metric
                  label="Storage used"
                  value={bytes(
                    officeData.chambers.reduce((n, c) => n + c.used, 0),
                  )}
                  sub="Measured file bytes"
                />
              </div>
              <div className="ws-two-col">
                <section className="ws-panel">
                  <PanelHead title="Chamber directory" note="LOCAL RECORDS" />
                  {officeData.chambers.map((c) => (
                    <div className="ws-admin-row" key={c.id}>
                      <span className="dc-avatar">
                        {c.name.slice(0, 2).toUpperCase()}
                      </span>
                      <div>
                        <b>{c.name}</b>
                        <small>
                          {c.plan} · {c.members} members
                        </small>
                      </div>
                      <span>
                        {bytes(c.used)}
                        <small>of {bytes(c.quota)}</small>
                      </span>
                    </div>
                  ))}
                </section>
                <section className="ws-panel ws-dark">
                  <PanelHead title="Service readiness" note="CONFIGURATION" />
                  {officeData.services.map((s) => (
                    <div className="ws-service" key={s.name}>
                      <span>{s.name}</span>
                      <small>{s.state}</small>
                    </div>
                  ))}
                </section>
              </div>
              <AuditPanel events={officeData.audit} />
            </>
          ) : view === "briefing" ? (
            <>
              <Heading
                eyebrow="YOUR DAILY BRIEFING"
                title={`Good to see you, ${me.user.name.split(" ")[0]}.`}
                detail="A quieter start. A clear next step. Your chamber is ready."
                action={
                  <button className="ws-button" onClick={() => edit("matter")}>
                    <Plus size={16} />
                    New matter
                  </button>
                }
              />
              <div className="ws-briefing-grid">
                <section className="ws-day-card">
                  <div className="ws-day-top">
                    <span>THE WORK AHEAD</span>
                    <span>FOUNDING EDITION</span>
                  </div>
                  <h2>
                    A little more prepared.
                    <br />
                    <em>A little more at ease.</em>
                  </h2>
                  <p>
                    Begin with a matter. Bring the record together.
                    <br />
                    Give your next thought a place to land.
                  </p>
                  <button onClick={() => go("document")}>
                    Open your document library
                    <ArrowUpRight size={18} />
                  </button>
                  <Scale className="ws-watermark" strokeWidth={0.45} />
                </section>
                <section className="ws-next-card">
                  <small>NEXT IN THE DIARY</small>
                  {matters
                    .filter((m) => m.data.date)
                    .sort((a, b) =>
                      (a.data.date ?? "").localeCompare(b.data.date ?? ""),
                    )[0] ? (
                    (() => {
                      const m = matters
                        .filter((m) => m.data.date)
                        .sort((a, b) =>
                          (a.data.date ?? "").localeCompare(b.data.date ?? ""),
                        )[0];
                      return (
                        <>
                          <div className="ws-date-large">
                            {new Date(m.data.date + "T12:00:00").getDate()}
                            <span>
                              {new Date(
                                m.data.date + "T12:00:00",
                              ).toLocaleDateString("en-IN", {
                                month: "long",
                                year: "numeric",
                              })}
                            </span>
                          </div>
                          <h3>{m.title}</h3>
                          <p>{m.data.court}</p>
                          <button onClick={() => openMatter(m)}>
                            Open matter
                            <ArrowRight size={15} />
                          </button>
                        </>
                      );
                    })()
                  ) : (
                    <p>Add a dated matter to see it here.</p>
                  )}
                </section>
              </div>
              <div className="ws-metrics">
                <Metric
                  label="Matters in your chamber"
                  value={String(matters.length).padStart(2, "0")}
                  sub="Every case in context"
                />
                <Metric
                  label="Open actions"
                  value={String(
                    tasks.filter((t) => t.data.status !== "done").length,
                  ).padStart(2, "0")}
                  sub="Your next clear steps"
                />
                <Metric
                  label="Documents"
                  value={String(documents.length).padStart(2, "0")}
                  sub={`${bytes(me.chamber?.used ?? 0)} of ${bytes(me.chamber?.quota ?? 5e9)} used`}
                />
              </div>
              <div className="ws-two-col">
                <section className="ws-panel">
                  <PanelHead
                    title="Your next actions"
                    action={
                      <button onClick={() => go("task")}>
                        All tasks <ArrowUpRight size={13} />
                      </button>
                    }
                  />
                  {tasks
                    .filter((t) => t.data.status !== "done")
                    .slice(0, 4)
                    .map((t) => (
                      <TaskRow
                        key={t.id}
                        task={t}
                        matter={
                          matters.find((m) => m.id === t.matter_id)?.title
                        }
                        toggle={() =>
                          mutate(
                            () =>
                              api(
                                "records/" + t.id,
                                send(
                                  {
                                    kind: t.kind,
                                    title: t.title,
                                    matter_id: t.matter_id,
                                    data: { ...t.data, status: "done" },
                                  },
                                  "PATCH",
                                ),
                              ),
                            "Task completed.",
                          )
                        }
                        edit={() => setEditing(t)}
                      />
                    ))}
                  {!tasks.length && (
                    <Empty
                      title="A clear desk."
                      text="Add your first task to begin."
                    />
                  )}
                </section>
                <section className="ws-panel">
                  <PanelHead
                    title="Current matters"
                    action={
                      <button onClick={() => go("matter")}>
                        All matters <ArrowUpRight size={13} />
                      </button>
                    }
                  />
                  {matters.slice(0, 3).map((m) => (
                    <button
                      className="ws-matter-mini"
                      onClick={() => openMatter(m)}
                      key={m.id}
                    >
                      <BriefcaseBusiness size={18} />
                      <span>
                        <b>{m.title}</b>
                        <small>{m.data.client || "Client not yet added"}</small>
                      </span>
                      <ChevronRight size={15} />
                    </button>
                  ))}
                </section>
              </div>
              <p className="ws-preview-note">
                Imported case benches begin as pending until the advocate confirms
                whether each matter is active, closed or archived.
              </p>
            </>
          ) : selected && view === "matter" ? (
            <>
              <button className="ws-back" onClick={() => setSelected(null)}>
                ← Matter register
              </button>
              <Heading
                eyebrow={selected.data.number || "MATTER WORKSPACE"}
                title={selected.title}
                detail={[selected.data.client, selected.data.court]
                  .filter(Boolean)
                  .join(" · ")}
                action={
                  <button
                    className="ws-button secondary"
                    onClick={() =>
                      setEditing(
                        items.find((i) => i.id === selected.id) ?? selected,
                      )
                    }
                  >
                    <PenLine size={15} />
                    Edit matter
                  </button>
                }
              />
              <div className="ws-tabs">
                {[
                  "overview",
                  "documents",
                  "notes",
                  "timeline",
                  "research",
                  "arguments",
                  "tasks",
                ].map((t) => (
                  <button
                    key={t}
                    className={tab === t ? "active" : ""}
                    onClick={() => setTab(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {tab === "overview" ? (
                <div className="ws-two-col">
                  <section className="ws-panel ws-padding">
                    <small>THE MATTER AT A GLANCE</small>
                    <h2>
                      {(items.find((i) => i.id === selected.id) ?? selected)
                        .data.body ||
                        "Add your matter description and preparation notes."}
                    </h2>
                    <div className="ws-detail-pairs">
                      <p>
                        Preparation{" "}
                        <b>
                          {(items.find((i) => i.id === selected.id) ?? selected)
                            .data.stage ?? "intake"}
                        </b>
                      </p>
                      <p>
                        Responsible person{" "}
                        <b>
                          {(items.find((i) => i.id === selected.id) ?? selected)
                            .data.assignee || "Not assigned"}
                        </b>
                      </p>
                      <p>
                        Status{" "}
                        <b>
                          {
                            (
                              items.find((i) => i.id === selected.id) ??
                              selected
                            ).data.status
                          }
                        </b>
                      </p>
                      <p>
                        Next date{" "}
                        <b>
                          {date(
                            (
                              items.find((i) => i.id === selected.id) ??
                              selected
                            ).data.date,
                          )}
                        </b>
                      </p>
                    </div>
                  </section>
                  <section className="ws-panel ws-padding">
                    <small>CONNECTED TO THIS MATTER</small>
                    {[
                      ["document", "Documents"],
                      ["note", "Notes"],
                      ["argument", "Arguments"],
                      ["task", "Tasks"],
                    ].map(([k, l]) => (
                      <p className="ws-count-row" key={k}>
                        {l}
                        <b>
                          {
                            active.filter(
                              (i) =>
                                i.kind === k && i.matter_id === selected.id,
                            ).length
                          }
                        </b>
                      </p>
                    ))}
                  </section>
                </div>
              ) : (
                <section className="ws-panel">
                  <PanelHead
                    title={tab.charAt(0).toUpperCase() + tab.slice(1)}
                    action={
                      <button
                        className="ws-button secondary"
                        onClick={() =>
                          tab === "documents"
                            ? setUpload(true)
                            : edit(
                                (
                                  {
                                    notes: "note",
                                    timeline: "event",
                                    research: "research",
                                    arguments: "argument",
                                    tasks: "task",
                                  } as Record<string, string>
                                )[tab],
                                selected.id,
                              )
                        }
                      >
                        <Plus size={14} />
                        Add {tab === "documents" ? "document" : "entry"}
                      </button>
                    }
                  />
                  {active
                    .filter(
                      (i) =>
                        i.matter_id === selected.id &&
                        i.kind ===
                          (
                            {
                              documents: "document",
                              notes: "note",
                              timeline: "event",
                              research: "research",
                              arguments: "argument",
                              tasks: "task",
                            } as Record<string, string>
                          )[tab],
                    )
                    .map((i) => (
                      <RecordRow
                        key={i.id}
                        item={i}
                        open={() =>
                          i.kind === "document"
                            ? setPreviewing(i)
                            : setEditing(i)
                        }
                        trash={() =>
                          mutate(
                            () =>
                              api(
                                "records/" + i.id,
                                send({ deleted: true }, "PATCH"),
                              ),
                            "Moved to trash.",
                          )
                        }
                      />
                    ))}
                  {!active.some(
                    (i) =>
                      i.matter_id === selected.id &&
                      i.kind ===
                        (
                          {
                            documents: "document",
                            notes: "note",
                            timeline: "event",
                            research: "research",
                            arguments: "argument",
                            tasks: "task",
                          } as Record<string, string>
                        )[tab],
                  ) && (
                    <Empty
                      title="Make space for the record."
                      text="Add your first entry to this matter."
                    />
                  )}
                </section>
              )}
            </>
          ) : view === "settings" ? (
            <>
              <Heading
                eyebrow="CHAMBER SETTINGS"
                title="The foundations of your workspace."
                detail="A clear view of your plan, storage and connected services."
              />
              <div className="ws-two-col">
                {me.user.access === "temporary" && <section className="ws-panel ws-padding">
                  <ShieldCheck />
                  <h2>Secure your permanent login</h2>
                  <p>Use the temporary access once to claim this existing chamber. We will email the permanent invitation, then Cognito will guide you through a new password and authenticator setup.</p>
                  {!claimResult ? <form className="ws-form" onSubmit={async event => {
                    event.preventDefault(); setBusy(true); setError("");
                    try {
                      const result = await api<{ email: string; message?: string }>("access/claim", send({ email: claimEmail }));
                      setClaimResult(result);
                    } catch (e) { setError(e instanceof Error ? e.message : "Unable to start secure access."); }
                    finally { setBusy(false); }
                  }}>
                    <label>Verified sign-in email<input type="email" autoComplete="email" value={claimEmail} onChange={event => setClaimEmail(event.target.value)} placeholder="name@gmail.com or name@outlook.com" required maxLength={254}/></label>
                    <button className="ws-button" disabled={busy}>{busy ? "Sending invitation…" : "Send secure invitation"}</button>
                  </form> : <div>
                    <p className="dc-note">Invitation sent to <b>{claimResult.email}</b>. The current temporary password remains active until the first successful secure sign-in.</p>
                    <Link className="ws-button" href="/api/auth/start" prefetch={false}>Set password and authenticator <ArrowRight size={15}/></Link>
                  </div>}
                </section>}
                <section className="ws-panel ws-padding">
                  <Cloud />
                  <h2>
                    Your {bytes(me.chamber?.quota ?? 5e9)} chamber allowance.
                  </h2>
                  <p>
                    {bytes(me.chamber?.used ?? 0)} used. Retained files in trash
                    continue to count towards your allowance.
                  </p>
                  <div className="ws-storage-bar">
                    <span
                      style={{
                        width:
                          Math.min(
                            100,
                            ((me.chamber?.used ?? 0) /
                              (me.chamber?.quota ?? 5e9)) *
                              100,
                          ) + "%",
                      }}
                    />
                  </div>
                  <p className="dc-note">
                    Verified files remain in the company Google Drive archive.
                    Digital Chambers stores their protected index and opens each
                    file through the user&apos;s authorized Drive session.
                  </p>
                </section>
                <section className="ws-panel ws-padding">
                  <h2>Connections</h2>
                  {[
                    ["Company Google Drive", "1,556 indexed documents"],
                    ["Document access account", me.user.access === "cognito" ? "Secure identity active · provider connection follows" : "Available after permanent sign-in"],
                    ["AI research provider", "Bring-your-own account planned"],
                    ["Production identity & MFA", "Available"],
                  ].map(([service, state]) => (
                    <div className="ws-service" key={service}>
                      <span>{service}</span>
                      <small>{state}</small>
                    </div>
                  ))}
                  <Link className="ws-back" href="/security">
                    Read the current privacy boundaries →
                  </Link>
                </section>
              </div>
              <AuditPanel events={events} />
            </>
          ) : view === "profile" ? (
            <>
              <Heading
                eyebrow="PROFESSIONAL PROFILE · PRIVATE DRAFT"
                title="Your name. Thoughtfully presented."
                detail="Complete your professional information. Publication remains separate from your private work."
                action={
                  <button
                    className="ws-button"
                    onClick={() =>
                      setEditing(
                        active.find((i) => i.kind === "profile") ?? {
                          kind: "profile",
                          title: me.user.name,
                          data: { published: false },
                        },
                      )
                    }
                  >
                    <PenLine size={15} />
                    Edit profile
                  </button>
                }
              />
              <section className="ws-profile">
                <div className="ws-profile-banner" />
                <div className="ws-profile-body">
                  <span className="dc-avatar large">MJ</span>
                  <h2>
                    {active.find((i) => i.kind === "profile")?.title ??
                      me.user.name}
                  </h2>
                  <small>ADVOCATE · PROFILE DRAFT</small>
                  <p>
                    {active.find((i) => i.kind === "profile")?.data.body ||
                      "Add your biography, practice areas and enrolment details. Only advocate-approved, verified information will become public."}
                  </p>
                  <div className="ws-profile-tags">
                    {["practice", "jurisdiction", "enrolment"].map((k) => (
                      <span key={k}>
                        {k}:{" "}
                        {active.find((i) => i.kind === "profile")?.data[
                          k as keyof Data
                        ] || "Pending"}
                      </span>
                    ))}
                  </div>
                  <Link
                    className="ws-button secondary"
                    href="/advocates/mahesh-jagtap"
                  >
                    View the public concept <ArrowUpRight size={15} />
                  </Link>
                  <p className="dc-note">
                    Private edits do not publish automatically. The public
                    concept remains unchanged.
                  </p>
                </div>
              </section>
            </>
          ) : view === "client" ? (
            <ClientLedger clients={active.filter((i) => i.kind === "client")} query={query} setQuery={setQuery} edit={setEditing} create={() => edit("client")} />
          ) : view === "research" ? (
            <>
              <Heading
                eyebrow="RESEARCH & PREPARATION"
                title="Think with the record beside you."
                detail="Find source passages and keep your analysis in context."
                action={
                  <button
                    className="ws-button"
                    onClick={() => edit("research")}
                  >
                    <Plus size={16} />
                    Save research note
                  </button>
                }
              />
              <div className="ws-two-col">
                <section className="ws-panel ws-padding">
                  <BookOpen />
                  <h2>Begin with a question.</h2>
                  <form
                    className="ws-research-form"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const data = new FormData(e.currentTarget);
                      await mutate(
                        async () =>
                          setResearch(
                            await api(
                              "research",
                              send({
                                question: data.get("question"),
                                matter: data.get("matter") || null,
                              }),
                            ),
                          ),
                        "Source search complete.",
                      );
                    }}
                  >
                    <select name="matter" aria-label="Research context">
                      <option value="">My chamber</option>
                      {matters.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.title}
                        </option>
                      ))}
                    </select>
                    <textarea
                      name="question"
                      minLength={2}
                      maxLength={1000}
                      placeholder="Find passages about the delivery date…"
                      required
                    />
                    <button className="ws-button" disabled={busy}>
                      Search my sources <ArrowRight size={15} />
                    </button>
                  </form>
                  {research && (
                    <div className="ws-research-results">
                      <p>{research.message}</p>
                      {research.sources.map((s) => (
                        <article key={s.id}>
                          <button onClick={() => {
                            const source = active.find((item) => item.id === s.id);
                            if (source) setPreviewing(source);
                          }}>
                            {s.title}
                            <ArrowUpRight size={14} />
                          </button>
                          <blockquote>{s.excerpt}</blockquote>
                        </article>
                      ))}
                    </div>
                  )}
                  <p className="dc-note">
                    Keyword retrieval from uploaded TXT files. No AI answer,
                    legal conclusion or invented authority is generated.
                  </p>
                </section>
                <section className="ws-panel">
                  <PanelHead title="Saved research" note="YOUR WORKING NOTES" />
                  {active
                    .filter((i) => i.kind === "research")
                    .map((i) => (
                      <RecordRow
                        key={i.id}
                        item={i}
                        open={() => setEditing(i)}
                        trash={() =>
                          mutate(
                            () =>
                              api(
                                "records/" + i.id,
                                send({ deleted: true }, "PATCH"),
                              ),
                            "Moved to trash.",
                          )
                        }
                      />
                    ))}
                  {!active.some((i) => i.kind === "research") && (
                    <Empty
                      title="A fresh research desk."
                      text="Save a question, authority or working note here."
                    />
                  )}
                </section>
              </div>
            </>
          ) : (
            <>
              <Heading
                eyebrow={
                  view === "document"
                    ? "THE CHAMBER RECORD"
                    : view === "task"
                      ? "YOUR NEXT CLEAR STEP"
                      : "CONNECTED TO YOUR PRACTICE"
                }
                title={
                  {
                    matter: "Every matter, in context.",
                    document: "The record. Always at hand.",
                    task: "The next step is never buried.",
                    client: "The people behind the matters.",
                  }[view] ?? title
                }
                detail={
                  view === "document"
                    ? "Your verified Drive archive, organised into matters and review queues."
                    : "Create, review and return to the work in your chamber."
                }
                action={
                  <button className="ws-button" onClick={() => view === "document" ? setNotice("New files enter through the reviewed Drive intake queue.") : edit(view)}>
                    {view === "document" ? (
                      <Upload size={16} />
                    ) : (
                      <Plus size={16} />
                    )}{" "}
                    {view === "document" ? "Drive intake" : `New ${view}`}
                  </button>
                }
              />
              <div className="ws-toolbar">
                <label>
                  <Search size={16} />
                  <input
                    aria-label="Search records"
                    placeholder={`Search ${view === "document" ? "documents and text" : view + "s"}…`}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </label>
                {view === "document" && (
                  <>
                    <select
                      aria-label="Filter by matter"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                    >
                      <option value="">All matters</option>
                      {matters.map((m) => (
                        <option value={m.id} key={m.id}>
                          {m.title}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() =>
                        setEditing({
                          kind: "folder",
                          title: "",
                          data: { parent: folder },
                        })
                      }
                    >
                      <Plus size={14} />
                      Folder
                    </button>
                  </>
                )}
                <button
                  className={trash ? "active" : ""}
                  onClick={() => setTrash(!trash)}
                >
                  <Trash2 size={14} />
                  {trash ? "Show active" : "Trash"}
                </button>
              </div>
              {view === "document" && !trash && (
                <div className="ws-folders">
                  <button onClick={() => setFolder(null)}>
                    <FolderOpen size={15} />
                    All files
                  </button>
                  {folder && (
                    <button
                      onClick={() =>
                        setFolder(
                          active.find((i) => i.id === folder)?.data.parent ??
                            null,
                        )
                      }
                    >
                      ↑ Parent folder
                    </button>
                  )}
                  {active
                    .filter(
                      (i) =>
                        i.kind === "folder" &&
                        (i.data.parent ?? null) === folder,
                    )
                    .map((f) => (
                      <button key={f.id} onClick={() => setFolder(f.id)}>
                        <FolderOpen size={15} />
                        {f.title}
                        <ChevronRight size={12} />
                      </button>
                    ))}
                  {folder && (
                    <span>
                      Inside {items.find((i) => i.id === folder)?.title}
                    </span>
                  )}
                </div>
              )}
              <section className="ws-panel">
                <div className="ws-table-label">
                  <span>
                    {view === "document"
                      ? "DOCUMENT / MATTER"
                      : view.toUpperCase()}
                  </span>
                  <span>{list.length} ENTRIES{list.length > 200 ? " · SHOWING 200" : ""}</span>
                </div>
                {list.slice(0, 200).map((i) =>
                  view === "task" && !trash ? (
                    <TaskRow
                      key={i.id}
                      task={i}
                      matter={matters.find((m) => m.id === i.matter_id)?.title}
                      edit={() => setEditing(i)}
                      toggle={() =>
                        mutate(
                          () =>
                            api(
                              "records/" + i.id,
                              send(
                                {
                                  kind: i.kind,
                                  title: i.title,
                                  matter_id: i.matter_id,
                                  data: {
                                    ...i.data,
                                    status:
                                      i.data.status === "done"
                                        ? "open"
                                        : "done",
                                  },
                                },
                                "PATCH",
                              ),
                            ),
                          "Task updated.",
                        )
                      }
                    />
                  ) : (
                    <RecordRow
                      key={i.id}
                      item={i}
                      subtitle={
                        matters.find((m) => m.id === i.matter_id)?.title
                      }
                      open={() =>
                        view === "matter"
                          ? openMatter(i)
                          : view === "document"
                            ? setPreviewing(i)
                            : setEditing(i)
                      }
                      edit={() => setEditing(i)}
                      trash={() =>
                        mutate(
                          () =>
                            api(
                              "records/" + i.id,
                              send({ deleted: !trash }, "PATCH"),
                            ),
                          trash ? "Record restored." : "Moved to trash.",
                        )
                      }
                    />
                  ),
                )}
                {!list.length && (
                  <Empty
                    title={
                      query
                        ? "Nothing matches your search."
                        : trash
                          ? "Your trash is empty."
                          : "A place for your first " + view + "."
                    }
                    text={
                      query
                        ? "Try a different name, matter or phrase."
                        : view === "document"
                          ? "Upload a sample file, or choose a folder to begin."
                          : "Use the action above to add an entry."
                    }
                  />
                )}
              </section>
              {view === "document" && (
                <p className="ws-preview-note">
                  Verified Drive quarantine · source paths preserved · status-review groups are not claimed as active cases
                </p>
              )}
            </>
          )}
        </main>
      </div>
      <Dialog open={command} onOpenChange={setCommand}>
        <DialogContent className="ws-dialog">
          <DialogTitle>Find your next step</DialogTitle>
          <DialogDescription>
            Search matters, documents or navigate the workspace.
          </DialogDescription>
          <label className="dc-search">
            <Search size={18} />
            <input
              autoFocus
              placeholder="Search your chamber…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          {!office &&
            nav
              .filter(([, label]) =>
                label.toLowerCase().includes(query.toLowerCase()),
              )
              .map(([id, label, I]) => (
                <button
                  className="ws-command-result"
                  key={id}
                  aria-label={label}
                  onClick={() => go(id)}
                >
                  <I size={17} />
                  {label}
                  <ArrowRight size={14} />
                </button>
              ))}
          {!office &&
            query &&
            active
              .filter((i) =>
                i.title.toLowerCase().includes(query.toLowerCase()),
              )
              .slice(0, 5)
              .map((i) => (
                <button
                  className="ws-command-result"
                  key={i.id}
                  onClick={() => {
                    setCommand(false);
                    if (i.kind === "matter") openMatter(i);
                    else if (i.kind === "document") setPreviewing(i);
                    else setEditing(i);
                  }}
                >
                  <FileText size={16} />
                  {i.title}
                </button>
              ))}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="ws-dialog">
          <DialogTitle>
            {editing?.id ? "Edit" : "New"}{" "}
            {editing?.kind === "provision" ? "chamber" : editing?.kind}
          </DialogTitle>
          <DialogDescription>
            Changes are saved to this private chamber and appear across devices after refresh.
          </DialogDescription>
          {error && (
            <p className="ws-error" role="alert">
              {error}
            </p>
          )}
          {editing && (
            <form
              key={editing.id ?? editing.kind}
              className="ws-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                const data: Data = { ...editing.data };
                for (const key of [
                  "assignee",
                  "stage",
                  "client",
                  "court",
                  "number",
                  "status",
                  "date",
                  "priority",
                  "body",
                  "practice",
                  "jurisdiction",
                  "enrolment",
                  "parent",
                  "case_numbers",
                  "payment_advance",
                  "payment_credit",
                  "payment_amount",
                  "recorded_dates",
                  "payment_status",
                  "location",
                ])
                  if (f.has(key))
                    Object.assign(data, { [key]: f.get(key) || "" });
                if (editing.kind === "client" && f.has("case_count")) data.case_count = Number(f.get("case_count"));
                if (editing.kind === "profile") data.published = false;
                const title = String(f.get("title"));
                const matter_id =
                  String(f.get("matter_id") ?? editing.matter_id ?? "") || null;
                await mutate(async () => {
                  if (editing.kind === "provision")
                    await api("office", send({ name: title }));
                  else if (editing.kind === "document")
                    await api("records/" + editing.id, send({ kind: "document", title, matter_id, data: { ...data, folder: String(f.get("folder") ?? "") || null } }, "PATCH"));
                  else
                    await api(
                      "records" + (editing.id ? "/" + editing.id : ""),
                      send(
                        { kind: editing.kind, title, matter_id, data },
                        editing.id ? "PATCH" : "POST",
                      ),
                    );
                  setEditing(null);
                }, "Saved to your workspace.");
              }}
            >
              <label>
                {editing.kind === "profile" ? "Professional name" : "Title"}
                <input
                  name="title"
                  defaultValue={editing.title}
                  required
                  maxLength={200}
                />
              </label>
              {!["matter", "client", "profile", "folder", "provision"].includes(
                editing.kind ?? "",
              ) && (
                <label>
                  Matter
                  <select
                    name="matter_id"
                    defaultValue={editing.matter_id ?? ""}
                  >
                    <option value="">Chamber-wide</option>
                    {matters.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.title}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {editing.kind === "matter" && (
                <>
                  <div className="ws-form-grid">
                    <label>
                      Client
                      <input
                        name="client"
                        defaultValue={editing.data?.client}
                        maxLength={200}
                        list="client-names"
                      />
                      <datalist id="client-names">
                        {active
                          .filter((i) => i.kind === "client")
                          .map((c) => (
                            <option key={c.id} value={c.title} />
                          ))}
                      </datalist>
                    </label>
                    <label>
                      Court
                      <input
                        name="court"
                        defaultValue={editing.data?.court}
                        maxLength={200}
                      />
                    </label>
                  </div>
                  <div className="ws-form-grid">
                    <label>
                      Case number
                      <input
                        name="number"
                        defaultValue={editing.data?.number}
                      />
                    </label>
                    <label>
                      Status
                      <select
                        name="status"
                        defaultValue={editing.data?.status ?? "active"}
                      >
                        {["active", "pending", "closed", "archived"].map(
                          (s) => (
                            <option key={s}>{s}</option>
                          ),
                        )}
                      </select>
                    </label>
                  </div>
                </>
              )}
              {editing.kind === "client" && (
                <>
                  <p className="ws-form-hint">These fields are the chamber&apos;s working ledger. Check imported amounts against source receipts before treating them as reconciled payments.</p>
                  <label>Connected case numbers<textarea name="case_numbers" rows={2} maxLength={6000} defaultValue={editing.data?.case_numbers}/></label>
                  <label>Case benches in source register<input name="case_count" type="number" min="0" step="1" defaultValue={editing.data?.case_count ?? 0}/></label>
                  <div className="ws-form-grid"><label>Recorded advance<input name="payment_advance" maxLength={200} defaultValue={editing.data?.payment_advance}/></label><label>Recorded credit<input name="payment_credit" maxLength={200} defaultValue={editing.data?.payment_credit}/></label></div>
                  <div className="ws-form-grid"><label>Amount field<input name="payment_amount" maxLength={200} defaultValue={editing.data?.payment_amount}/></label><label>Payment status<input name="payment_status" maxLength={120} defaultValue={editing.data?.payment_status} placeholder="For advocate review"/></label></div>
                  <label>Dates from client register<textarea name="recorded_dates" rows={2} maxLength={1000} defaultValue={editing.data?.recorded_dates}/></label>
                  <label>Contact / office location<input name="location" maxLength={500} defaultValue={editing.data?.location}/></label>
                </>
              )}
              {["matter", "task"].includes(editing.kind ?? "") && (
                <label>
                  Responsible person
                  <input
                    name="assignee"
                    maxLength={120}
                    defaultValue={editing.data?.assignee}
                    placeholder="Name from your chamber team"
                  />
                </label>
              )}
              {editing.kind === "matter" && (
                <label>
                  Preparation stage
                  <select
                    name="stage"
                    defaultValue={editing.data?.stage ?? "intake"}
                  >
                    {[
                      "intake",
                      "research",
                      "drafting",
                      "filing",
                      "hearing",
                      "order",
                      "closed",
                    ].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </label>
              )}
              {["matter", "task", "event"].includes(editing.kind ?? "") && (
                <label>
                  {editing.kind === "matter" ? "Next hearing" : "Date"}
                  <input
                    type="date"
                    name="date"
                    defaultValue={editing.data?.date}
                  />
                </label>
              )}
              {editing.kind === "event" && (
                <><label>Court or venue<input name="court" maxLength={200} defaultValue={editing.data?.court}/></label><label>Diary status<select name="status" defaultValue={editing.data?.status || "open"}><option value="open">Open</option><option value="done">Completed</option><option value="closed">Closed</option></select></label></>
              )}
              {editing.kind === "task" && (
                <label>
                  Priority
                  <select
                    name="priority"
                    defaultValue={editing.data?.priority ?? "normal"}
                  >
                    {["normal", "high", "urgent"].map((p) => (
                      <option key={p}>{p}</option>
                    ))}
                  </select>
                </label>
              )}
              {editing.kind === "document" && (
                <label>
                  Folder
                  <select
                    name="folder"
                    defaultValue={editing.data?.folder ?? ""}
                  >
                    <option value="">Root</option>
                    {active
                      .filter((i) => i.kind === "folder")
                      .map((f) => (
                        <option value={f.id} key={f.id}>
                          {f.title}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              {editing.kind === "profile" && (
                <>
                  {["practice", "jurisdiction", "enrolment"].map((k) => (
                    <label key={k}>
                      {k}
                      <input
                        name={k}
                        defaultValue={String(
                          editing.data?.[k as keyof Data] ?? "",
                        )}
                        maxLength={200}
                      />
                    </label>
                  ))}
                </>
              )}
              {!["document", "folder", "provision"].includes(
                editing.kind ?? "",
              ) && (
                <label>
                  {editing.kind === "argument"
                    ? "Proposition, facts, authorities and counterargument"
                    : "Notes / description"}
                  <textarea
                    rows={5}
                    name="body"
                    defaultValue={editing.data?.body}
                    maxLength={20000}
                  />
                </label>
              )}
              <button className="ws-button" disabled={busy}>
                {busy
                  ? "Saving…"
                  : "Save " +
                    (editing.kind === "provision" ? "chamber" : editing.kind)}
                <Check size={16} />
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!previewing} onOpenChange={(open) => !open && setPreviewing(null)}>
        <DialogContent className="ws-document-preview">
          <DialogTitle>{previewing?.title ?? "Document preview"}</DialogTitle>
          <DialogDescription>Private Google Drive preview. Your authorized Drive session controls access.</DialogDescription>
          {previewing && <iframe title={previewing.title} src={`/api/documents/${previewing.id}?preview=1`} />}
          {previewing && <a className="ws-button secondary" href={`/api/documents/${previewing.id}`} target="_blank" rel="noreferrer"><ArrowUpRight size={15}/>Open in Google Drive</a>}
        </DialogContent>
      </Dialog>
      <Dialog open={upload} onOpenChange={setUpload}>
        <DialogContent className="ws-dialog">
          <DialogTitle>Bring a document into your chamber.</DialogTitle>
          <DialogDescription>
            Sample files only. Up to 20 MB per file; stored on your SSD.
          </DialogDescription>
          {error && (
            <p className="ws-error" role="alert">
              {error}
            </p>
          )}
          <form
            className="ws-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              await mutate(async () => {
                await api("documents", { method: "POST", body: f });
                setUpload(false);
              }, "Document uploaded and saved.");
            }}
          >
            <label className="ws-upload-zone">
              <Upload size={25} />
              <span>Choose a document</span>
              <input
                type="file"
                name="file"
                required
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg"
              />
            </label>
            <label>
              Matter
              <select name="matter" defaultValue={selected?.id ?? filter}>
                <option value="">Chamber-wide</option>
                {matters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Folder
              <select name="folder" defaultValue={folder ?? ""}>
                <option value="">Root</option>
                {active
                  .filter((i) => i.kind === "folder")
                  .map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.title}
                    </option>
                  ))}
              </select>
            </label>
            <button className="ws-button" disabled={busy}>
              {busy ? "Uploading…" : "Upload document"}
              <ArrowRight size={16} />
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function ClientLedger({ clients, query, setQuery, edit, create }: { clients: Item[]; query: string; setQuery: (value: string) => void; edit: (item: Item) => void; create: () => void }) {
  const rows = clients.filter((client) => (client.title + " " + (client.data.case_numbers ?? "")).toLowerCase().includes(query.toLowerCase()));
  return <>
    <Heading eyebrow="CLIENT AND ACCOUNTS REGISTER" title="Clients, cases and recorded payments." detail="Review imported entries and add clients as your practice grows. Financial values require advocate reconciliation." action={<button className="ws-button" onClick={create}><Plus size={16}/>New client</button>} />
    <div className="ws-metrics">
      <Metric label="Client identities" value={String(clients.length).padStart(2, "0")} sub="Exact register names; no automatic merging" />
      <Metric label="Connected case benches" value={String(clients.reduce((n, item) => n + (item.data.case_count ?? 0), 0)).padStart(2, "0")} sub="All begin pending" />
      <Metric label="Date review" value="Required" sub="No hearing date inferred from filenames" />
    </div>
    <div className="ws-toolbar"><label><Search size={16}/><input aria-label="Search clients and case numbers" placeholder="Search clients or case numbers…" value={query} onChange={(e) => setQuery(e.target.value)} /></label></div>
    <section className="ws-panel ws-client-ledger">
      <div className="ws-table-label"><span>CLIENT / CASES / ACCOUNTS</span><span>{rows.length} ENTRIES{rows.length > 200 ? " · SHOWING 200" : ""}</span></div>
      {rows.slice(0, 200).map((client) => <button key={client.id} onClick={() => edit(client)} aria-label={`Edit client ${client.title}`}>
        <span className="dc-avatar">{client.title.slice(0,2).toUpperCase()}</span>
        <span><b>{client.title}</b><small>{client.data.case_count ?? 0} case benches · {client.data.case_numbers || "Numbers to review"}</small></span>
        <span><small>RECORDED ADVANCE</small><b>{client.data.payment_advance || "Review"}</b></span>
        <span><small>RECORDED CREDIT</small><b>{client.data.payment_credit || "Review"}</b></span>
        <span><small>AMOUNT FIELD</small><b>{client.data.payment_amount || "Review"}</b></span>
        <span><small>DATES</small><b>{client.data.recorded_dates || "Review source"}</b></span>
        <ChevronRight size={15}/>
      </button>)}
      {!rows.length && <Empty title="No client matches." text="Try a different client name or case number." />}
    </section>
    <p className="ws-preview-note">Payment values are imported register entries, not invoices, receipts or reconciled balances. Mahesh can review and correct each client record.</p>
  </>;
}
function LockCard({ message, office }: { message: string; office: boolean }) {
  return (
    <section>
      <ShieldCheck />
      <h1>{office ? "Administration access." : "Your chamber awaits."}</h1>
      <p>{message || "Sign in to enter your private workspace."}</p>
      <Link
        className="dc-action"
        href={"/sign-in" + (office ? "?office=1" : "")}
      >
        Continue to sign in
        <ArrowRight size={16} />
      </Link>
      <Link className="ws-back" href="/">
        Return to the public website
      </Link>
    </section>
  );
}
function Heading({
  eyebrow,
  title,
  detail,
  action,
}: {
  eyebrow: string;
  title: string;
  detail: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="ws-heading">
      <div>
        <p>{eyebrow}</p>
        <h1>{title}</h1>
        <span>{detail}</span>
      </div>
      {action}
    </div>
  );
}
function Metric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="ws-metric">
      <small>{label}</small>
      <strong>{value}</strong>
      <span>{sub}</span>
    </div>
  );
}
function PanelHead({
  title,
  note,
  action,
}: {
  title: string;
  note?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="ws-panel-head">
      <div>
        {note && <small>{note}</small>}
        <h2>{title}</h2>
      </div>
      {action}
    </div>
  );
}
function Empty({ title, text }: { title: string; text: string }) {
  return (
    <div className="ws-empty">
      <FolderOpen strokeWidth={1} />
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}
function TaskRow({
  task,
  matter,
  toggle,
  edit,
}: {
  task: Item;
  matter?: string;
  toggle: () => void;
  edit: () => void;
}) {
  return (
    <div className="ws-task">
      <button
        className={`ws-check ${task.data.status === "done" ? "done" : ""}`}
        aria-label={
          task.data.status === "done"
            ? "Reopen " + task.title
            : "Complete " + task.title
        }
        onClick={toggle}
      >
        {task.data.status === "done" && <Check size={13} />}
      </button>
      <button onClick={edit} className="ws-task-title">
        <b className={task.data.status === "done" ? "completed" : ""}>
          {task.title}
        </b>
        <small>
          {matter ?? "Chamber-wide"} · {date(task.data.date)}
          {task.data.assignee ? " · " + task.data.assignee : ""}
        </small>
      </button>
      <span className="ws-tag">{task.data.priority}</span>
    </div>
  );
}
function RecordRow({
  item,
  subtitle,
  open,
  edit,
  trash,
}: {
  item: Item;
  subtitle?: string;
  open: () => void;
  edit?: () => void;
  trash: () => void;
}) {
  return (
    <div className="ws-record">
      <button
        className="ws-record-main"
        onClick={open}
        disabled={!!item.deleted}
      >
        <span className="ws-record-icon">
          {item.kind === "matter" ? (
            <BriefcaseBusiness size={20} />
          ) : item.kind === "client" ? (
            <UserRound size={20} />
          ) : (
            <FileText size={20} />
          )}
        </span>
        <span>
          <b>{item.title}</b>
          <small>
            {subtitle || item.data.client || item.data.court || item.kind}
            {item.data.size !== undefined ? " · " + bytes(item.data.size) : ""}
            {item.data.date ? " · " + date(item.data.date) : ""}
          </small>
        </span>
      </button>
      <div className="ws-row-actions">
        {item.data.status && <span className="ws-tag">{item.data.status}</span>}
        {item.kind === "document" && !item.deleted && (
          <a
            className="ws-icon-button"
            title="Open verified file in Google Drive"
            aria-label={"Open " + item.title + " in Google Drive"}
            href={"/api/documents/" + item.id}
          >
            <Download size={16} />
          </a>
        )}
        {edit && !item.deleted && (
          <IconButton label={"Edit " + item.title} onClick={edit}>
            <PenLine size={15} />
          </IconButton>
        )}
        <IconButton
          label={
            item.deleted
              ? "Restore " + item.title
              : "Move " + item.title + " to trash"
          }
          onClick={trash}
        >
          {item.deleted ? <RotateCcw size={15} /> : <Trash2 size={15} />}
        </IconButton>
      </div>
    </div>
  );
}
function AuditPanel({ events }: { events: Audit[] }) {
  return (
    <section className="ws-panel ws-audit">
      <PanelHead title="Activity record" note="ACCOUNTABLE ACTIONS" />
      {events.slice(0, 12).map((e, i) => (
        <div key={i}>
          <Activity size={14} />
          <span>{e.action.replaceAll(".", " · ")}</span>
          <small>
            {new Date(e.created).toLocaleString("en-IN", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </small>
        </div>
      ))}
      {!events.length && (
        <Empty
          title="A new activity record."
          text="Saved actions will appear here."
        />
      )}
    </section>
  );
}
